/**
 * Actions: how a person's hand reaches the door.
 *
 * Every operation the playground sends on someone's behalf goes through
 * `run`: a confirm where the brief asks for one (destructive, money, server),
 * then the beats of the one grammar, intent → flight → door → verdict. The
 * seal is not played here: it comes from the journal, through the
 * reconciler, whoever caused it. The answer nudges the reconciler, peers get
 * `act` frames, and a refusal brings its fix-it cards, keyed by `refusal.by`.
 *
 * Session-key safety lives here too. Revoking the key this tab uses ends the
 * session, rotating it moves the tab to the successor, and a 401 sends the
 * tab back to Connect (`sessionVerdict`, `guardSession`).
 *
 * The decisions are pure functions over CallRecords and Args, tested without
 * a DOM; `createActionsWith` only wires them to the client, the conductor
 * and whatever UI main.ts hands in.
 */

import type {
  ActOptions, Actions, Args, Attribution, BeatSource, CallRecord, CarryState, Catalogue, Client, ConfirmRequest, CreateActions, Cue,
  FixIt, Flip, Held, Hole, Json, KeySafety, Operation, Origin, Persona, PresenceIn, RecordKind, Security, Source,
  Target, Unsub, Verb, WorldMatter,
} from '../contracts';
import { runs } from '../../../console/src/grid';
import { scrubs } from '../gesture/targets';
import { refusalCue, type FixContext } from '../model/refusal';
import { isPersonaKey, worldKey } from '../worlds';
import { confirmOf, type ConfirmKind } from '../spec/confirm';
import { MISSING, resolvePointer } from '../spec/nouns';
import { viaFor } from './transport';
import { isObj } from '../model/json';
import { readSlots, slotsOf } from '../model/masks';

export type { KeySafety } from '../contracts';

// ---------- small readers over Json ----------

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** The request body inside Args (MCP arguments carry it under `body`). */
export const bodyOf = (args: Args): Record<string, Json> => (isObj(args.body) ? args.body : {});

/** The World an act is about: the `w` parameter, else the target's. */
export const worldOf = (args: Args, target?: Target | null): string | undefined => str(args.w) ?? target?.world ?? undefined;

/** A position as the API names it back, from a body's `date` (+ `hour`) or its raw form. */
export const positionOf = (body: Record<string, Json>): { parametric: number; coordinates: number[] } | null => {
  const date = str(body.date);
  if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split('-').map(Number) as [number, number, number];
    const hour = num(body.hour);
    return { parametric: y, coordinates: hour === null ? [m - 1, d] : [m - 1, d, hour] };
  }
  const parametric = num(body.parametric);
  if (parametric !== null && Array.isArray(body.coordinates)) {
    return { parametric, coordinates: body.coordinates.filter((c): c is number => typeof c === 'number') };
  }
  return null;
};

/** The address key the tracked matter uses for a position, or null when it is not in view. */
export const keyAt = (matter: WorldMatter | undefined, pos: { parametric: number; coordinates: readonly number[] } | null): number | null => {
  if (!matter || !pos) return null;
  for (const p of matter.byKey.values()) {
    if (p.parametric === pos.parametric && p.coordinates.length === pos.coordinates.length
      && p.coordinates.every((c, i) => c === pos.coordinates[i])) return p.key;
  }
  return null;
};

// ---------- what asks before it acts ----------

/** The sheet's words: what will happen, in plain terms, and what the button says. */
export const confirmRequestFor = (op: string, args: Args, kind: ConfirmKind, ownKeyId: string | null): ConfirmRequest => {
  const body = bodyOf(args);
  const id = str(args.id) ?? '';
  const own = id !== '' && id === ownKeyId;
  switch (op) {
    case 'revokeKey':
      return {
        kind, title: `Revoke key ${id}?`, confirmLabel: 'Revoke',
        body: own
          ? 'This is the key this tab is using. Revoking it ends your session here and returns to Connect.'
          : 'Anything using this key stops working at once. Revoking cannot be undone.',
      };
    case 'rotateKey': {
      const grace = num(body.graceSeconds) ?? 0;
      return {
        kind, title: `Rotate key ${id}?`, confirmLabel: 'Rotate',
        body: `A successor key is minted and its secret shown once. The old key ${grace > 0 ? `keeps working for ${grace} s` : 'stops at once'}.`
          + (own ? ' This tab uses it: the session moves to the successor on its own.' : ''),
      };
    }
    case 'revokeGrant':
      return { kind, title: `Revoke grant ${id}?`, confirmLabel: 'Revoke', body: 'The other workspace loses what it could see or ask here.' };
    case 'deleteWebhook':
      return { kind, title: `Delete webhook ${id}?`, confirmLabel: 'Delete', body: 'Events stop posting to its URL.' };
    case 'giveGrant': {
      const can = Array.isArray(body.can) ? body.can.join(' and ') : 'see';
      const world = str(body.world) ?? '*';
      return {
        kind, title: `Let ${str(body.to) ?? 'that workspace'} ${can} ${world === '*' ? 'every World' : world}?`, confirmLabel: 'Grant',
        body: 'They can read these Worlds' + (can.includes('ask') ? ' and write proposed planes into them through relay asks' : '') + ' until you revoke the grant.',
      };
    }
    case 'createWebhook':
      return { kind, title: `Post events to ${str(body.url) ?? 'that URL'}?`, confirmLabel: 'Create', body: 'Every chosen event is posted there, signed. The signing secret (whsec_) is shown once.' };
    case 'checkout':
      return { kind, title: `Buy the ${str(body.plan) ?? ''} plan?`.replace('  ', ' '), confirmLabel: 'Continue to checkout', body: 'This opens a checkout for real money. Nothing is charged until you complete it there.' };
    case 'portal':
      return { kind, title: 'Open the billing portal?', confirmLabel: 'Open portal', body: 'The portal can change or cancel the paid subscription.' };
    case 'mockBillingAct':
      return { kind, title: `Mock billing: ${str(args.act) ?? 'act'}?`, confirmLabel: 'Send', body: 'Dev only. It changes this workspace\'s plan and subscription state as a real payment would.' };
    case 'stripeWebhook':
      return { kind, title: 'Call Stripe\'s webhook yourself?', confirmLabel: 'Send anyway', body: 'Stripe calls this, server to server. Unsigned, it answers 400, or 503 where Stripe is not configured.' };
    default:
      return { kind, title: `Run ${op}?`, confirmLabel: 'Run', body: 'This changes your account.' };
  }
};

// ---------- holes a draft still needs ----------


const fixedValue = (args: Args, key: string): Json | typeof MISSING =>
  key.startsWith('/') ? resolvePointer(args, key) : (bodyOf(args)[key] ?? args[key] ?? MISSING);

/** The required, visible holes `args` leaves empty, the matching variant's included. */
export const missingHoles = (op: Operation, args: Args): Hole[] => {
  const variant = op.variants.find((v) => Object.keys(v.fixed).length > 0
    && Object.entries(v.fixed).every(([k, value]) => JSON.stringify(fixedValue(args, k)) === JSON.stringify(value)));
  const seen = new Set<string>();
  const out: Hole[] = [];
  for (const hole of [...op.holes, ...(variant?.holes ?? [])]) {
    if (!hole.required || hole.hidden || seen.has(hole.pointer)) continue;
    seen.add(hole.pointer);
    if (resolvePointer(args, hole.pointer) === MISSING) out.push(hole);
  }
  return out;
};

// ---------- the beats ----------

export const beatSourceOf = (source: Source, origin: Origin): BeatSource => {
  switch (source.kind) {
    case 'director': return { kind: 'director', assistId: source.assistId };
    case 'scenario': return { kind: 'scenario', run: source.run };
    default: return { kind: 'me', at: origin };
  }
};

/**
 * Which road a drop came by, from the carry it ended: the keyboard (or a verb
 * ring), a click that carried, or a pointer drag. The hand hands every drop an
 * origin, a keyboard one included, so the origin alone cannot tell; it decides
 * only when no carry is known.
 */
export const viaOf = (phase: CarryState['phase'] | undefined, origin: Origin): 'pointer' | 'click' | 'keyboard' => {
  if (phase === 'keyboard' || phase === 'ring') return 'keyboard';
  if (phase === 'carrying') return 'click';
  if (phase === 'dragging' || phase === 'pressed') return 'pointer';
  return origin ? 'pointer' : 'keyboard';
};

/** Which peers' label an act frame carries; `auto` reads send none. */
export const presenceVia = (source: Source): 'hand' | 'director' | 'scenario' | null =>
  source.kind === 'hand' ? 'hand' : source.kind === 'director' ? 'director' : source.kind === 'scenario' ? 'scenario' : null;

interface ProblemView { title: string; detail: string }
const problemOf = (body: Json): ProblemView => ({
  title: isObj(body) ? str(body.title) ?? 'Error' : 'Error',
  detail: isObj(body) ? str(body.detail) ?? '' : typeof body === 'string' ? body : '',
});

/** The head an answer carries: the header, else the body's. */
export const headOf = (record: CallRecord): string | null => {
  const a = record.answer;
  if (!a) return null;
  return a.head ?? (isObj(a.body) ? str(a.body.head) : null);
};

/**
 * What a verdict is read against: the World's matter, records and the view (CORE's FixContext),
 * a position key when the caller knows it better, and whether the act came from the keyboard.
 */
export interface VerdictLook extends FixContext { readonly key?: number | null; readonly keyboard?: boolean }

/**
 * The verdict beat for an answered call: a receipt (and its consequences),
 * a refusal with its shield and fix-its, or an error. Pure.
 */
export const verdictCues = (id: string, op: string, record: CallRecord, look: VerdictLook = {}): Cue[] => {
  const a = record.answer;
  if (!a) return [];
  // The World as the stage keys it: the persona that called, and its World's name.
  const name = str(record.args.w);
  const world = name ? worldKey(record.persona, name) : null;
  if (a.status === 0) return [{ kind: 'error', id, status: 0, title: 'Unreachable', detail: problemOf(a.body).detail || 'the API could not be reached' }];
  if (a.status < 300) {
    const cues: Cue[] = [{ kind: 'receipt', id, status: a.status, head: headOf(record) }];
    const b = a.body;
    const moved = isObj(b) && ((Array.isArray(b.displaced) && b.displaced.length > 0) || (Array.isArray(b.told) && b.told.length > 0));
    if (moved && world) cues.push({ kind: 'consequence', world, data: b });
    return cues;
  }
  const refused = world ? refusalCue(record, look) : null;
  if (refused && world) {
    const key = refused.key >= 0 ? refused.key : look.key ?? -1;
    return [{ ...refused, id, world, key, ...(look.keyboard ? { focus: true } : {}) }];
  }
  const p = problemOf(a.body);
  return [{
    kind: 'error', id, status: a.status, title: p.title, detail: p.detail,
    ...(a.status === 429 && a.rate ? { retryAfter: a.rate.reset } : {}),
  }];
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** One sentence for the aria-live voice: "claim by sara on w, 2026-10-05, slots 9–10: receipt #12". */
export const describeAct = (op: string, args: Args, record: CallRecord | null): string => {
  const body = bodyOf(args);
  const who = str(body.who) ?? (str(body.from) && str(body.to) ? `${body.from} to ${body.to}` : null);
  const hour = num(body.hour);
  const date = str(body.date);
  const slots = readSlots(body.slots);
  const head = `${op}${who ? ` by ${who}` : ''}${str(args.w) ? ` on ${args.w}` : ''}`
    + `${date ? `, ${date}${hour !== null ? ` ${pad2(hour)}h` : ''}` : ''}${slots ? `, slots ${runs(slotsOf(slots))}` : ''}`;
  const a = record?.answer;
  if (!a) return head;
  if (a.status === 0) return `${head}: unreachable`;
  if (a.status < 300) {
    const seq = isObj(a.body) ? num(a.body.commitSeq) : null;
    return `${head}: ${seq !== null ? `receipt #${seq}` : `${a.status}`}`;
  }
  const p = problemOf(a.body);
  return `${head}: ${a.status === 409 ? 'refused' : `${a.status} ${p.title}`}${p.detail ? `, ${p.detail}` : ''}`;
};

/** Account calls re-list what they changed (§5.5 refetch table; §7 rule 13 on 404 and 409). */
export const ACCOUNT_RECORDS: Readonly<Record<string, readonly RecordKind[]>> = {
  createKey: ['keys'], revokeKey: ['keys'], rotateKey: ['keys'],
  giveGrant: ['grants'], revokeGrant: ['grants'],
  createWebhook: ['webhooks'], deleteWebhook: ['webhooks'],
  checkout: ['billing'], portal: ['billing'], mockBillingAct: ['billing', 'usage'],
};

// ---------- session-key safety ----------

export type SessionVerdict =
  | { readonly kind: 'ended' }
  | { readonly kind: 'rotated'; readonly secret: string; readonly id: string | null }
  | { readonly kind: 'unauthorized'; readonly persona: Persona }
  | null;

const KEY_SECRET = /^sbk_[A-Za-z0-9_]+$/;

/** What an answered call means for the keys this tab holds (§7 rule 16). */
export const sessionVerdict = (record: CallRecord, security: Security | undefined, ownKeyId: string | null): SessionVerdict => {
  const a = record.answer;
  if (!a) return null;
  if (a.status === 401 && (security ?? 'key') === 'key') return { kind: 'unauthorized', persona: record.persona };
  if (a.status >= 300 || ownKeyId === null) return null;
  const id = str(record.args.id);
  if (record.op === 'revokeKey' && id === ownKeyId) return { kind: 'ended' };
  if (record.op === 'rotateKey' && isObj(a.body)) {
    const retired = isObj(a.body.retired) ? str(a.body.retired.id) : null;
    const secret = str(a.body.secret);
    if ((id === ownKeyId || retired === ownKeyId) && secret && KEY_SECRET.test(secret)) {
      return { kind: 'rotated', secret, id: str(a.body.id) };
    }
  }
  return null;
};

const apply = (v: SessionVerdict, hooks: KeySafety): void => {
  if (!v) return;
  if (v.kind === 'ended') hooks.ended('This tab\'s key was revoked, so the session has ended.');
  else if (v.kind === 'rotated') hooks.rotated(v.secret, v.id);
  else hooks.unauthorized(v.persona);
};

/** Watch every answered call (hand, Director, scenario) for the three key events. */
export const guardSession = (client: Client, catalogue: () => Catalogue, ownKeyId: () => string | null, hooks: KeySafety): Unsub =>
  client.on((record, phase) => {
    if (phase !== 'answered') return;
    apply(sessionVerdict(record, catalogue().byId(record.op)?.security, ownKeyId()), hooks);
  });

// ---------- the Actions ----------

export interface VerdictCard {
  readonly id: string;
  readonly op: string;
  readonly kind: 'refusal' | 'error';
  readonly eyebrow: string;
  readonly detail: string;
  readonly fixits: readonly FixIt[];
  readonly retryAfter?: number;
  readonly origin: Origin;
  /** The act came from the keyboard: the card takes focus, so its fix-its are one Tab away. */
  readonly keyboard: boolean;
  /** For a refusal: where it fell, for the race subtitle (§7 rule 5). */
  readonly world?: string;
  readonly key?: number;
  readonly slots?: bigint;
  readonly by?: string;
}

/**
 * What only a DOM can do: the carried ghost and the verb ring (the contract's), and a verdict's
 * words as a card, for whoever says more about it (main.ts voices the race line). The refusal
 * itself, its sentence and fix-its, is the conductor's to draw.
 */
export interface ActionsUi {
  ghost(held: Held | null, at?: { x: number; y: number }, home?: boolean): void;
  ring(verbs: readonly Verb[], subject: Held | Target): void;
  verdict?(card: VerdictCard): void;
}

export type ActionsDeps = Omit<Parameters<CreateActions>[0], 'ui'> & {
  readonly ui?: ActionsUi;
  /** Where the view is, for fix-its whose call named no position (find windows). */
  readonly focus?: () => { readonly date?: string; readonly hour?: number };
};

/** The card a refusal or an error leaves at the drop point. */
export const verdictCardOf = (cue: Cue, op: string, origin: Origin, keyboard = false): VerdictCard | null => {
  if (cue.kind === 'refusal') {
    return {
      id: cue.id, op, kind: 'refusal', eyebrow: `REFUSED · ${cue.by.toUpperCase()}`, detail: cue.detail, fixits: cue.fixits, origin, keyboard,
      world: cue.world, key: cue.key, slots: cue.slots, by: cue.by,
    };
  }
  if (cue.kind === 'error') {
    return {
      id: cue.id, op, kind: 'error', eyebrow: cue.status === 0 ? 'UNREACHABLE' : `${cue.status} · ${cue.title.toUpperCase()}`,
      detail: cue.detail, fixits: [], origin, keyboard, ...(cue.retryAfter !== undefined ? { retryAfter: cue.retryAfter } : {}),
    };
  }
  return null;
};

/** Hands whose acts come from the keyboard: their verdict card takes focus. */
export const fromKeyboard = (source: Source): boolean =>
  source.kind === 'hand' && (source.via === 'keyboard' || source.via === 'palette');

// ---------- races (§7 rule 5) ----------

/** A commit sealed on one position, remembered briefly so a refusal can name who got there first. */
export interface Seal {
  readonly world: string;
  readonly key: number;
  /** The bits it set on held planes. */
  readonly set: bigint;
  readonly seq: number;
  readonly by: Attribution;
  readonly at: number;
}

/** The seals in one batch of flips: one per commit and position, held bits only. */
export const sealsOf = (flips: readonly Flip[], by: Attribution, at: number): Seal[] => {
  const out = new Map<string, { world: string; key: number; set: bigint; seq: number }>();
  for (const f of flips) {
    if (f.cause.kind !== 'commit' || !f.plane.endsWith('|held') || f.set === 0n) continue;
    const id = `${f.world}|${f.key}|${f.cause.seq}`;
    const had = out.get(id);
    out.set(id, { world: f.world, key: f.key, seq: f.cause.seq, set: (had?.set ?? 0n) | f.set });
  }
  return [...out.values()].map((s) => ({ ...s, by, at }));
};

/** How long a seal stays a candidate for "sealed first". */
export const RACE_MS = 3_000;
/** How long a refusal waits for the winner's seal that has not landed yet. */
export const RACE_WAIT_MS = 300;

/**
 * The seal that beat a refused claim: the latest one, on the same World (and
 * position, when the refusal knows it), that set any of the refused slots,
 * within RACE_MS. Only `held` and `capacity` refusals are races.
 */
export const raceSeal = (seals: readonly Seal[], card: Pick<VerdictCard, 'world' | 'key' | 'slots' | 'by'>, now: number): Seal | null => {
  if (!card.world || !card.slots || (card.by !== 'held' && card.by !== 'capacity')) return null;
  let best: Seal | null = null;
  for (const s of seals) {
    if (s.world !== card.world || now - s.at > RACE_MS || (s.set & card.slots) === 0n) continue;
    if (card.key !== undefined && card.key >= 0 && s.key !== card.key) continue;
    if (!best || s.at >= best.at) best = s;
  }
  return best;
};

/** "sealed first (seq 12) · from Sara's tab (unverified)". */
export const raceLine = (seal: Seal, peerName: (sid: string) => string | null): string => {
  const src = seal.by.source;
  const from = src.kind === 'peer' ? `from ${peerName(src.sid) ?? 'another'}'s tab (unverified)`
    : src.kind === 'hand' ? 'from this tab'
      : src.kind === 'director' ? 'by the Director'
        : src.kind === 'scenario' ? 'by a scenario run'
          : src.kind === 'auto' ? 'from this tab' : null;
  return `sealed first (seq ${seal.seq})${from ? ` · ${from}` : ''}`;
};

export const createActionsWith = (deps: ActionsDeps): Actions => {
  let n = 0;
  const conductor = deps.fx.conductor;

  /** The World a write lands on (an MCP tools/call by its tool's arguments), keyed by persona; null for reads. */
  const writeWorld = (op: string, args: Args, persona: Persona | undefined): string | null => {
    let id = op;
    let a: Record<string, Json> = args as Record<string, Json>;
    if (op === 'mcp') {
      const b = bodyOf(args);
      const p = isObj(b.params) ? b.params : {};
      if (b.method !== 'tools/call' || typeof p.name !== 'string') return null;
      id = p.name;
      a = isObj(p.arguments) ? p.arguments : {};
    }
    const o = deps.catalogue().byId(id);
    if (!o || o.method === 'GET' || typeof a.w !== 'string') return null;
    return isPersonaKey(a.w) ? a.w : worldKey(persona ?? 'A', a.w);
  };

  const run = async (op: string, args: Args, opts: ActOptions = {}): Promise<CallRecord | null> => {
    const operation = deps.catalogue().byId(op);
    const ownKeyId = deps.ownKeyId?.() ?? null;
    const kind = opts.confirm ?? confirmOf(operation, args);
    const label = opts.label ?? op;
    if (kind) {
      const ok = await deps.confirm(confirmRequestFor(op, args, kind, ownKeyId));
      if (!ok) {
        deps.announce.polite(`${label}: not sent`);
        return null;
      }
    }
    // §5.5 rule 9: a write never lands on a World scrubbed back in time; whatever road it came by
    // (a drop, the Deck's Fire, ⌘⏎, the MCP lens), the World returns to Live first.
    const writing = writeWorld(op, args, opts.persona);
    if (writing && scrubs.seq(writing) !== null) scrubs.live(writing);
    n += 1;
    const id = `act-${n}`;
    const source: Source = opts.source ?? { kind: 'hand', via: opts.origin ? 'pointer' : 'keyboard' };
    const origin = opts.origin ?? null;
    const beat = beatSourceOf(source, origin);
    const target = opts.target ?? null;
    // The World as the stage keys it: `w` by name with a persona given is that persona's World.
    const named = worldOf(args, target);
    const world = named && opts.persona && opts.persona !== 'A' && named === str(args.w) && !isPersonaKey(named) ? worldKey(opts.persona, named) : named;
    const writes = operation ? operation.method !== 'GET' : true;
    const said = writes ? presenceVia(source) : null;

    conductor.play({ kind: 'intent', id, op, source: beat, target });
    conductor.play({ kind: 'flight', id, op, source: beat, target, cls: operation?.cls ?? (writes ? 'write' : 'read') });
    conductor.play({ kind: 'door', id });
    if (said) deps.presence({ t: 'act', ph: 'sent', op, call: id, via: said }, world);

    const via = opts.via ?? (deps.transport ? viaFor(operation, deps.transport.get()) : undefined);
    const record = await deps.client.call(op, args, {
      source, origin,
      ...(opts.persona ? { persona: opts.persona } : {}),
      ...(via ? { via } : {}),
    });
    const a = record.answer;
    const status = a?.status ?? 0;
    // A secret shown once reaches the one who asked for it: the record as the caller gets it.
    if (a && status < 300) deps.secret?.(record);

    // The World as the stores key it: a persona's World by its key.
    const key = typeof record.args.w === 'string' ? worldKey(record.persona, record.args.w) : undefined;
    const me = deps.records?.get(null, 'me');
    const view = deps.focus?.() ?? {};
    const look: VerdictLook = {
      ...(key && deps.planes?.get(key) ? { matter: deps.planes.get(key)! } : {}),
      ...(isObj(me) && typeof me.workspace === 'string' ? { workspace: me.workspace } : {}),
      ...(view.date ? { focus: { date: view.date, ...(view.hour !== undefined ? { hour: view.hour } : {}) } } : {}),
      ...(key && deps.records?.get(key, 'asks') !== undefined ? { asks: deps.records.get(key, 'asks')! } : {}),
      ...(key && deps.records?.get(key, 'relays') !== undefined ? { relays: deps.records.get(key, 'relays')! } : {}),
      keyboard: fromKeyboard(source),
    };
    const cues = verdictCues(id, op, record, look);
    for (const cue of cues) conductor.play(cue);

    if (said) {
      const head = headOf(record);
      const settled: PresenceIn = { t: 'act', ph: 'settled', op, call: id, status, via: said, ...(head ? { head } : {}) };
      deps.presence(settled, world);
    }
    if (world) deps.reconciler.nudge(world, { kind: 'answer', record });
    const account = ACCOUNT_RECORDS[op];
    if (account && deps.records && (status < 300 || status === 404 || status === 409) && status !== 0) {
      for (const k of account) void deps.records.refresh(null, k);
    }
    if (deps.keySafety) apply(sessionVerdict(record, operation?.security, ownKeyId), deps.keySafety);

    const quiet = source.kind === 'auto';
    const keyboard = fromKeyboard(source);
    const card = cues.map((c) => verdictCardOf(c, op, origin, keyboard)).find((c) => c !== null) ?? null;
    if (card && !quiet) {
      deps.ui?.verdict?.(card);
      const fixes = card.fixits.length > 0
        ? ` Fixes: ${card.fixits.map((f) => f.label).join(', ')}.${keyboard ? ' Focus is on the first; Tab moves between them, Esc goes back.' : ''}`
        : '';
      deps.announce.assertive(`${card.kind === 'refusal' ? 'Refused' : card.eyebrow.toLowerCase()}: ${card.detail || describeAct(op, args, record)}.${fixes}`);
    } else if (!quiet && status > 0 && status < 300) {
      deps.announce.polite(opts.label && opts.label !== op ? `${opts.label}: ${describeAct(op, args, record)}` : describeAct(op, args, record));
    }
    return record;
  };

  const actions: Actions = {
    run,
    draft: (op, partial, need) => deps.openDraft(op, partial, need),
    effect(e) {
      switch (e.type) {
        case 'fire':
        case 'confirm': {
          // A page (the docs) is opened in a tab, as a click on it does: fetched here it would only fill the Wire.
          const page = deps.catalogue().byId(e.verb.op);
          if (page?.cls === 'page' && typeof window !== 'undefined' && typeof window.open === 'function') {
            try {
              const url = deps.client.twins(page.id, e.args).rest.url;
              if (/^https?:\/\//.test(url)) { window.open(url, '_blank', 'noopener,noreferrer'); deps.announce.polite(`Opened ${page.id} in a new tab`); return; }
            } catch { /* no address for it: it is called instead */ }
          }
          const carried = deps.carry?.();
          const over = carried?.over ?? null;
          const confirm = e.type === 'confirm' ? (e.verb.confirm ?? 'destructive') : e.verb.confirm;
          void run(e.verb.op, e.args, {
            origin: e.origin, target: over, source: { kind: 'hand', via: viaOf(carried?.phase, e.origin) },
            ...(confirm ? { confirm } : {}),
            ...(e.persona ? { persona: e.persona } : {}),
          });
          return;
        }
        case 'need':
          deps.openDraft(e.verb.op, e.partial, e.holes);
          return;
        case 'announce':
          if (e.assertive) deps.announce.assertive(e.text);
          else deps.announce.polite(e.text);
          return;
        case 'presence':
          deps.presence(e.frame, e.world);
          return;
        case 'ghost':
          deps.ui?.ghost(e.held, e.at, e.home);
          return;
        case 'ring':
          deps.ui?.ring(e.verbs, e.subject);
          return;
      }
    },
  };
  return actions;
};

/** The contract's factory. */
export const createActions: CreateActions = (deps) => createActionsWith(deps);

/** Run a fix-it card: straight to the door when its holes are filled, else open it as a draft. */
export const runFixit = (actions: Actions, catalogue: Catalogue, fixit: FixIt, origin: Origin = null, via: 'click' | 'keyboard' = 'click'): void => {
  const op = catalogue.byId(fixit.op);
  const missing = op ? missingHoles(op, fixit.args) : [];
  if (missing.length > 0) actions.draft(fixit.op, fixit.args, missing);
  else void actions.run(fixit.op, fixit.args, { origin, source: { kind: 'hand', via }, label: fixit.label });
};
